import ArticleCollection from '@domain/catalog/ArticleCollection'
import { i18nLabelInterface } from '@domain/catalog/catalog.d'
import ApiCaller from '../utils/apiCaller'
import Toggles from '@domain/toggles/Toggles'

export interface TechnicalData {
    baseColor: TechnicalDataColor[]
    color: TechnicalDataColor[]
    control: TechnicalDataAttribute[]
    cri: TechnicalDataAttribute[]
    installation: TechnicalDataAttribute[]
    material: TechnicalDataAttribute[]
    temperature: TechnicalDataAttribute[]
    colorMOQ: number
}

export type TechnicalDataAttribute = {
    description: i18nLabelInterface
    cri?: string
}
export type TechnicalDataColor = TechnicalDataAttribute & {
    label: string
    buttonColor: string
    ral: string
    hex?: string
    thumbnailImage?: string
}
class ArticleCollectionApi {
    constructor() { }

    static async getTechnicalInfo(subFamilyCode: number, catalogId: number, appLocation: number): Promise<TechnicalData> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfo?subFamily=${subFamilyCode}&catalog=${catalogId}&appLocation=${appLocation}`)
    }

    static async getTechnicalInfoByModel(familyCode: number, catalogId: number, modelId: number): Promise<any> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfoByModel?family=${familyCode}&catalog=${catalogId}&model=${modelId}`)

    }
    static async getSpotLights(subFamilyId: number, catalogId: number): Promise<any> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfoByModel/spotlight?catalog=${catalogId}&subFamily=${subFamilyId}`)
    }

    static async getAttachments(familyId: number, model: number, applicationLocationId: number): Promise<any> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfoByModel/attachments?family=${familyId}&model=${model}&appLocation=${applicationLocationId}`)
    }

    static async getModels(catalogId: number, applicationLocationId: number, subFamilyId: number, virtuals: 'only' | 'without' | 'included' = 'included'): Promise<any> {
        const virtualQuery = {
            only: 'virtual=1',
            without: 'virtual=0',
        }
        const query = [
            `catalog=${catalogId}`,
            `applicationLocationId=${applicationLocationId}`,
            `subFamilyId=${subFamilyId}`,
            virtualQuery[virtuals]
        ].filter(q => q)
        return await ApiCaller.call('v2', 'GET', `articles/listModelsByFamily?${query.join('&')}`)
    }

    static composeParameters(subFamily: any, applicationLocation: any, family: number, model: number | string, productType: string, files: any[]) {
        type Params = {
            familyId: number | string;
            model: number | string;
            subFamilyId?: string; 
            applicationLocationId: string;
            productType?: string;
            subFamily?: {
              id: number;
              description?: string;
            };
        };
          
        type Data = {
            type: string;
            subType?: string;
            params: Params;
        };
        const documentParams = files.map(type => {
            let documentType = type
            let data: Data = {
              type: documentType,
              params: {
                familyId: family,
                model: model,
                subFamilyId: subFamily,
                applicationLocationId: applicationLocation,
                productType: productType
              }
            };
            switch (type) {  
                case 'specSheet':
                    const modelType = Number(model);
                    data.subType = Math.floor(modelType) > 100 ? "model" : "umbrella";
                    delete data.params.subFamilyId;
                    data.params.subFamily = {
                        id: subFamily
                    }
                    break;  
                case 'manual':   
                    data.subType = 'REGULAR';
                    break;  
                case 'ambient':
                    data.type = 'imagesHD';
                    data.subType = 'ambient';
                    break;
                case 'product':
                    data.type = 'imagesHD';
                    data.subType = 'product';
                    break;
                case 'energyLabel':  
                    delete data.params.productType;
                    break;
                default:
                    break;
                }

            return data
        });
        return documentParams;
    }

    static async getDownloads(catalogId: string | number, subFamily: any, applicationLocation: any, family: number, model: number | string, token: string, productType: string, files: any[], language: string): Promise<any> {
        const fileConstructor = (filesArray: Array<string>) => {
            const headers = 'files[]='
            return headers + filesArray.join(`&${headers}`)
        }

        if (Toggles.isEnabled("docsWebV2MultipleDownloadsOnTheProductPage")) {
            const documentParams = this.composeParameters(subFamily, applicationLocation, family, model, productType, files);
            const lang = (language === 'en-US') ? 'us' : language;
            const serviceRoute = `documents/docs-web/global-generate?catalogId=${catalogId}&lang=${lang}`;
            return await ApiCaller.call('v2', 'POST', serviceRoute, documentParams, { credentials: 'include' });
        } else {
            return await ApiCaller.call('v2', 'GET', `documents/docs-web?catalog=${catalogId}&language=${language}&subfamily=${subFamily}&family=${family}&model=${model}&productType=${productType}&applicationLocation=${applicationLocation}&${fileConstructor(files)}`, null, { credentials: 'include' })
        }
    }

    static async getArticlesCollectionByModel(catalogId: string | number, model: string | number){
        return await ApiCaller.call('v2', 'GET', `article/search?catalogId=${catalogId}&search=${model}`)
    }

    static async getArticleCollection(models, catalog, subfamily?: number): Promise<any> {
        //TODO: Use v3 api
        //  return await ApiCaller.call('', 'GET', `catalog/catalog_${catalog}/subFamily_${subFamily}/appLocation_${appLocation}.json`, null, null, 'static.')
        let url = `articles/variations?${models.map(model => `model=${model}`).join('&')}&catalog=${catalog}`

        if (subfamily) {
            url = `articles/variations?${models.map(model => `model=${model}`).join('&')}&catalog=${catalog}&subfamily=${subfamily}`
        }
        return await ApiCaller.call('v2', 'GET', url)
    }

    static async getArticleCollectionInstance({ catalog, applicationLocation, subfamily }) {
        const response = await ArticleCollectionApi.getModels(catalog, applicationLocation, subfamily, 'without') || []
        if (response.message) {
            return
        }
        const models = response.find(elem => elem.catalogId == catalog)?.models || []
        const modelIds = models.map(model => model.model).sort((a, b) => a - b)

        const variations = await ArticleCollectionApi.getArticleCollection(modelIds, catalog, subfamily)
        if (!variations) return

        return ArticleCollection.getInstance(variations)
    }

}

export default ArticleCollectionApi